<script setup>
import { getImageUrl } from '@tools/media-image';
import { computed } from 'vue';
import ContactSlide from '@components/OfferCard/ContactSlide.vue';
import Labels from '@components/OfferCard/Labels.vue';
import MediaImage from '@components/MediaImage.vue';
import Carousel from '@components/OfferCard/Carousel.vue';
import HitType from '@components/OfferCard/HitType.vue';
import StreetDistrict from '@components/OfferCard/StreetDistrict.vue';
import OfferPrice from '@components/OfferCard/OfferPrice.vue';
import { trans } from '@tools/translation';
import { isOfferRentSurface } from '@organisms/offer/offer-utils';
import { PriceTypeMode } from '@enums/priceTypeMode';

const props = defineProps({
  firstOffer: {
    type: Object,
    required: true,
  },
  offers: {
    type: Array,
    default: () => [],
  },
  isSingleOpenspace: {
    type: Boolean,
    default: false,
  },
  url: {
    type: String,
    default: null,
  },
  selectionItem: {
    type: Object,
    default: null,
  },
  imageRatio: {
    type: Array,
    default: () => [ 290, 230 ],
  },
  width: {
    type: Number,
    default: 290,
  },
  carouselHeight: {
    type: Number,
    default: 230,
  },
  isOwnerView: {
    type: Boolean,
    default: false,
  },
  isContacted: {
    type: Boolean,
    default: false,
  },
  target: {
    type: String,
    default: '_blank',
  },
  priceSuffix: {
    type: String,
    default: PriceTypeMode.SHORT,
  },
});

const firstStationDistance = computed(() => props.firstOffer.first_station_distance);

const promoteTypeCode = computed(() => props.firstOffer.promote_types?.[0]?.code);

const hasCarousel = computed(() => !props.ownerView);

const sliderImages = computed(() => props.firstOffer.slider_images);

const offerUrl = computed(() => {
  if (props.url === null && !props.offers.length) {
    return props.firstOffer.public_url;
  }

  return props.url;
});

const clearImages = computed(() => {
  if (hasCarousel.value) {
    return sliderImages.value.slice(0, 12);
  }
  return [ sliderImages.value[0] ] ?? [];
});

const hasMoreMedia = computed(() => hasCarousel.value && sliderImages.value.length > 12);

const contactImage = computed(() => {
  if (hasMoreMedia.value) {
    return sliderImages.value[12];
  }
  return sliderImages.value[sliderImages.value.length - 1] ?? null;
});

const contactText = computed(() => trans(hasMoreMedia.value ? 'offer_card.contact_slide.more_pictures' : 'offer_card.contact_slide.know_more'));

const displayedInfos = computed(() => (props.offers.length <= 1 || !props.selectionItem ? props.firstOffer.offer_infos : props.selectionItem.offers_infos));

const displayedPrice = computed(() => {
  if (props.offers.length <= 1 || !props.selectionItem) {
    if (isOfferRentSurface(props.firstOffer)) {
      return props.firstOffer.monthly_total_price ?? 0;
    }

    return props.firstOffer.total_price;
  }

  return props.selectionItem.total_price;
});

const openOffer = ((event) => {
  if (props.url === null && props.offers.length) {
    event.preventDefault();
  }
});
</script>

<template>
  <article class="real-estate-card offer-card">
    <a
      class="offer-card__link"
      :href="offerUrl"
      :target="target"
      :title="firstOffer.name"
      @click="openOffer($event)"
    >
      <div class="offer-card__slider offer-card__slider--flickity">
        <Labels
          :offer="firstOffer"
          :proposal="selectionItem"
          :is-owner-view="isOwnerView"
          :is-contacted="isContacted"
        />

        <div class="offer-card__actions">
          <slot name="actions" />
        </div>

        <Carousel :height="carouselHeight">
          <MediaImage
            v-for="uuid in clearImages"
            :key="uuid"
            :ratio="imageRatio"
            :width="width"
            :uuid="uuid"
            :alt="firstOffer.name"
            is-lazy-flickity
          />
          <ContactSlide
            v-if="hasCarousel && contactImage"
            :uuid="contactImage"
            :text="contactText"
            :link="url ?? firstOffer.public_url"
          />
        </Carousel>
      </div>

      <div
        v-if="$slots.body"
        class="offer-card__body"
      >
        <slot name="body" />
      </div>

      <div v-else class="offer-card__infos">
        <div class="offer-card__container">
          <span
            v-if="promoteTypeCode"
            :class="`offer-card__container__promote-type offer-card__container__promote-type--${promoteTypeCode}`"
          >
            {{ trans(`offer.promote_type.${promoteTypeCode}`) }}
          </span>

          <span v-if="isOwnerView" class="offer-card__offer-title">{{ firstOffer.name }}</span>

          <div class="offer-card__container__link">
            <StreetDistrict v-if="firstOffer.address !== null" :address="firstOffer.address" />

            <ul
              v-if="firstStationDistance && !isOwnerView"
              class="offer-card__transport"
            >
              <li
                v-for="unique_line in firstStationDistance.unique_lines"
                :key="unique_line"
                class="offer-card__transport__image"
              >
                <img
                  :src="getImageUrl('f13b1efb-2744-43a1-bc0d-d0ed4b5f96e8')"
                  :class="`icon-${unique_line}`"
                  :alt="unique_line"
                >
              </li>
            </ul>
          </div>

          <HitType
            :first-offer="firstOffer"
            :offers="offers"
            :is-owner-view="isOwnerView"
          />

          <div class="offer-card__container-infos">
            <div class="offer-card__container-infos__text">
              {{ displayedInfos }}
            </div>
          </div>

          <div v-if="!!$slots.services" class="offer-card__container__services">
            <slot name="services" />
          </div>

          <OfferPrice
            class="offer-card__container__price"
            :offer="firstOffer"
            :price="displayedPrice"
            :suffix="priceSuffix"
          />

          <div class="offer-card__container__body-footer">
            <slot name="body-footer" />
          </div>
        </div>
      </div>

      <div class="offer-card__footer">
        <slot name="footer" />
      </div>
    </a>
  </article>
</template>

<style lang="scss" scoped>
@use '@styles/components/offer-card';
</style>
